import cn from 'classnames';
import { tAnswer, tPlayTaskType } from 'common/modules/ReactShared';
import React, { useCallback, useMemo, useRef } from 'react';
import useHeartBeatPing from 'students/views/shared/components/HeartBeat/useHeartBeatPing';
import Prompt from 'students/views/shared/components/Prompt';
import { useExitPrompt, usePromptContext } from 'students/views/shared/hooks';
import styled from 'styled-components';

import NotSupportedTask from '../../NotSupportedTask';
import { tSubjectComponentProps } from '../types';
import TaskTransitioner from './TaskTransitioner';
import { playTaskMap } from './Tasks';
import { PlayPage } from './common/components';
import { PLAY_PAGE_DEFAULT_COLOR } from './common/components/PlayPage';
import useTaskTransitioner from './useTaskTransitioner';

export interface IPlaySubjectComponentProps extends tSubjectComponentProps {
  task: tPlayTaskType;
}

const PlayTaskContainer: React.FC<IPlaySubjectComponentProps> = ({
  lesson,
  task,
  taskSession,
  lessonPhrases,
  isLoading,
  isCompleting,
  onNext,
  onFinish,
  onFinishAndNext,
  onSubmitTaskItem
}) => {
  const TaskComponent = playTaskMap.get(task.type);
  const prevTaskIdRef = useRef(0);
  const {
    startButtonPositioned,
    setStartButtonPositioned,
    showBubbleAnimation,
    bubbleChanger
  } = useTaskTransitioner();

  const pingAction = useHeartBeatPing();
  const { isDirty, makeDirty } = usePromptContext();

  useExitPrompt(isDirty, pingAction);

  const handleNext = useCallback(() => {
    if (!isCompleting) {
      makeDirty(false);
      onNext();
      prevTaskIdRef.current = task.id;
      setStartButtonPositioned(false);
    }
  }, [isCompleting, onNext, task.id, setStartButtonPositioned, makeDirty]);

  const handleSkip = useCallback(() => {
    if (!isCompleting) {
      makeDirty(false);
      onFinishAndNext(taskSession?.taskItemSessions);
      prevTaskIdRef.current = task.id;
      setStartButtonPositioned(false);
    }
  }, [
    isCompleting,
    onFinishAndNext,
    task.id,
    taskSession,
    setStartButtonPositioned,
    makeDirty
  ]);

  const handleFinish = useCallback(
    (answers: tAnswer) => {
      if (!isCompleting) {
        makeDirty(false);
        onFinish(answers);
      }
    },
    [isCompleting, onFinish, makeDirty]
  );

  const taskBody = useMemo(() => {
    let currentTaskBody: JSX.Element | null;
    if (isLoading) {
      currentTaskBody = null;
    } else {
      currentTaskBody = (
        <>
          {TaskComponent ? (
            <TaskComponent
              className="ln-grow ln-pt-6"
              key={task.id}
              lessonPhrases={lessonPhrases}
              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              // @ts-ignore
              task={task}
              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              // @ts-ignore
              taskSession={taskSession}
              isCompleting={!!isCompleting}
              hasApiBgImage={!!(task.backgroundImageURL || lesson?.bgImageURL)}
              onFinish={handleFinish}
              onAnswer={onSubmitTaskItem}
              onNext={handleNext}
              onSkip={handleSkip}
            />
          ) : (
            <NotSupportedTask
              color={PLAY_PAGE_DEFAULT_COLOR}
              isCompleting={!!isCompleting}
              onNext={handleSkip}
            />
          )}

          {startButtonPositioned && bubbleChanger}
        </>
      );
    }

    return currentTaskBody;
  }, [
    TaskComponent,
    handleNext,
    handleSkip,
    handleFinish,
    onSubmitTaskItem,
    isCompleting,
    isLoading,
    task,
    taskSession,
    lessonPhrases,
    startButtonPositioned,
    bubbleChanger,
    lesson?.bgImageURL
  ]);

  return (
    <>
      {isLoading && (
        <TaskTransitioner
          showBubbleAnimation={showBubbleAnimation}
          loadingOnly={task.type === 'Tasks::WordGames'}
        />
      )}

      <PlayPage
        backgroundImageURL={task.backgroundImageURL || lesson?.bgImageURL}
        overlayColor={
          task.backgroundImageURL || lesson?.bgImageURL
            ? TaskComponent?.bgColor || PLAY_PAGE_DEFAULT_COLOR
            : undefined
        }
        backgroundComponent={TaskComponent?.backgroundComponent}
        className={cn({
          invisible:
            showBubbleAnimation &&
            prevTaskIdRef.current !== task.id &&
            !startButtonPositioned
        })}
      >
        <SPlayTaskContainer>{taskBody}</SPlayTaskContainer>
      </PlayPage>

      <Prompt />
    </>
  );
};

export default PlayTaskContainer;

const SPlayTaskContainer = styled.div`
  padding: 1rem;
  display: flex;
  flex-direction: column;
  flex-grow: 1;
`;
